import { pollingEventStatus, EventType } from '@/common/polling';
import { upgradeSuccessPage, wiseUpgradeSuccessPage } from '@/common/service/common';
import store from '@/store/index';
import { pageConfig } from '@/common/service/page-config';
import { planReactiveRequest, updatePlans } from '@/apis';
import { cloneDeep } from 'lodash';
import { omitBy } from 'lodash';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { getOrderSummaryFields } from '@/flows/utils';
import { UseVerificationTypes } from '@/store/modules/use-verification';
import { PBX_SUB_TYPES } from './computers/plans/pbx';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';
import { planTypes, PRO_PLUS_PLAN_CODES_SUPPORTED } from '../constants';
import { de$type } from './flow/utils';
import { ONLINE_NOT_SUPPORT_BILLING_CYCLE } from './non-standard-billing-cycle-utils';
import { plansof } from '@/utils/chain';
import { couponSourceTypeMap } from '@/views/config/coupon/helper/constant';
import { afterPurchaseSuccessHandlerInModalCheckout } from '@/utils/modal-utils';

export const UpdateTypes = {
  UPDATE_BASE: 1, // Update Zoom Meeting / BIZ Plus
  UPDATE_ADDON: 2, // Update other plans.
  UPDATE_PBX: 3,
  PRO_TO_BIZ: 4,
  MEETING_TO_BIZ_PLUS: 5, // upgrade pro/biz to biz plus
  PRO_TO_BIZ_PLUS: 5, // upgrade pro to biz plus
  BIZ_TO_BIZ_PLUS: 5, // upgrade biz to biz plus
  PRO_PLUS_TO_PRO_PLUS: 5, // upgrade pro plus to pro plus
  BIZ_TO_PRO: 6,
  BIZ_PLUS_TO_BIZ: 7, // downgrade biz plus to biz & biz plus to pro plus
  DOWNGRADE_TO_ZONE_BASIC_PLUS: 8,
  ZONE_BASIC_PLUS_UPGRADE_TO_MEETING: 9,
  UPDATE_TOLL_FREE: 10,
  BIZ_PLUS: 11,
  MIX_BUY_PARTNER: 12, // JUST FOR MIX BUY CASE (edit base && add addon mix case) mix buy partnership plan
  PRO_TO_PTO_PLUS: 13, // pro upgrade to pro plus & biz to pro plus & pbx to pro plus
  UPDATE_PRO_PLUS: 14, // edit pro plus
  ZS_TO_ZE: 15, // zs to ze
  SWITCH: 16, // switch plan (for example: ZE SaaS -> ZE PPA / ZS SaaS -> ZS PPA / w500 -> w100)
  EDU_SCH_CMP_PLUS_UPDATE: 18, // update edu school plus
  EDU_SCH_CMP_PLUS_DOWNGRADE: 17, // edu school plus => edu school
  EDU_SCH_CMP_UPGRADE: 19, // edu school => edu school plus
  ADD_TO_CART_UPDATE: 20, // edit base meeting plan in add-to-cart flow
  BIZ_TO_PRO_PLUS: 201,
  BIZ_PLUS_TO_PRO_PLUS: 211,
  EDU_ANNUAL_TO_SCH_CMP: 21,
};

export const removeEmptyValue = (obj) => omitBy(obj, (v) => v === '' || v === undefined || v === null);

export const getPaymentInfo = (getters) => {
  if (pageConfig.hasBillingZuoraId) {
    return {};
  }

  const apmInfo = getters.apmInfo;
  let { paymentResponse, paymentGateway } = getters.paymentMethodInfo;

  if (apmInfo.isApm) {
    paymentResponse = JSON.stringify({
      integrationType: APM,
    });
  }

  return {
    contact: cloneDeep([removeEmptyValue(getters.billToContact), removeEmptyValue(getters.soldToContact)]),
    paymentInfo: { paymentResponse, paymentGateway },
  };
};

export function setMultipleRatePlanFlag(updateForm) {
  const getters = store.getters;
  const boughtMultipleRatePlans = getters.boughtPlans.filter((bp) => bp.ratePlans?.length > 1);
  if (updateForm?.planList?.some(p => boughtMultipleRatePlans.some((bp) => (bp.plan_type && p.oldPlanType) ? bp.plan_type === p.oldPlanType : [p.oldPlanCode, p.planCode].includes(bp.plan_code)))) {
    updateForm.multiRatePlan = true;
  }
}

export const updatePlansCheckout = async (
  submitParams,
  planList,
  actionType,
  couponData,
  extensionUpdateData,
  multiRatePlan = false,
) => {
  const { paymentResponse } = store.getters.paymentMethodInfo;
  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  const previewType = store.getters.getPreviewType;

  const { apmInfo } = store.getters;
  const params = {
    actionType,
    planList,
    paymentResponse: paymentResponse,
    couponData,
    extensionUpdateData,
    multiRatePlan,
    preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };
  if (previewType) {
    params.previewType = previewType;
  }
  params.extensionUpdateData = addParamsIfNeedUseVerification(params.extensionUpdateData)
  addParamsIfApm(params);
  const result = await updatePlans(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  store.commit('setTransactionId', eventResult?.uniqueKey);
  if (!preview) {
    // todo need check params
    if (pageConfig?.skipSuccessPage) {
      if (window?.$context?.is?.modal) {
        afterPurchaseSuccessHandlerInModalCheckout();
      }
    }

    !pageConfig?.skipSuccessPage && // * if skipSuccessPage = true, not navigate to successpage, for example in modal checkout add license edit flow.
      (apmInfo.apmPaymentMethodName === WISE ?
        wiseUpgradeSuccessPage({ actionType }) :
        upgradeSuccessPage(null, actionType)
      );
  }

  return { eventResult };
};

export const addParamsIfApm = (params) => {
  if (pageConfig?.apmP2PEnabled && store.getters.apmInfo?.isApm) {
    params.apmData = {
      apmName: store.getters.apmInfo?.apmPaymentMethodName,
    };
    if (store.getters.apmInfo?.apmPaymentMethodName === WISE) {
      params.apmData.wisePaymentName = store.getters.apmInfo?.tileImageFileName;
    }
  }
};

export const reactivatePlan = async (meetingPlan) => {
  const postData = {
    plan_code: meetingPlan.plan_code,
    planType: meetingPlan.type,
  };
  let resp;
  try {
    resp = await planReactiveRequest(postData);
  } catch (e) {
    throw typeof e === 'string' ? e : e?.message;
  }
  if (resp?.result?.status === 1) {
    await pollingEventStatus({ eventType: EventType.BasePLanReactivateEvent });
    return true;
  }
};

export const addParamsIfNeedUseVerification = (extension) => {
  if (store.getters.needUseVerification) {
    if (!extension) {
      extension = {}
    }
    const businessVerification = {
      taxAccountNumber: store.getters.useVerificationDetail.taxAccountNumber
    }
    if (store.getters.useVerificationType === UseVerificationTypes.CIN) {
      businessVerification.cinId = store.getters.useVerificationDetail.cinId
    } else if (store.getters.useVerificationType === UseVerificationTypes.EDU) {
      businessVerification.eduId = store.getters.useVerificationDetail.eduId
    }
    extension.businessVerification = businessVerification
  }
  return extension
}

// Postoned to Dec release
// export const reactivateSubPlan = async (meetingPlan) => {
//   const postData = {
//     plan_code: meetingPlan.plan_code,
//     planType: meetingPlan.type,
//   };
//   let resp;
//   try {
//     resp = await planReactiveSubPlanRequest(postData);
//   } catch (e) {
//     throw typeof e === 'string' ? e : e?.message;
//   }
//   if (resp?.result?.status === 1) {
//     await pollingEventStatus({ eventType: EventType.AddonPlanReactivateEvent });
//     return true;
//   }
// }

export function getPlanCodeMap(getters, types) {
  const fromCampaignLink = getters.fromCampaignLink;
  if (!fromCampaignLink && !getters.plansofBizBundle.boughtBizPlan.length) {
    types.push(planTypes.ZOOM_MEETING);
  }

  return (plans) => {
      return plansof(plans)
      .type(...types.map(item => item))
      .get()
      .filter(p => !p.isFreeTrial)
      .map((item) => ({ ...item, planCode: item.plan_code, quantity: item.cartInfo.selected_number }))
      .reduce((prev, cur) => {
          prev[cur.planCode] = [cur.quantity, cur.category, cur.plan_type, cur.billing_cycle];
          return prev;
      }, {});
  }
}

export function getUnsupportBillingCycleCategoryMap (boughtPlansMap, selectedPlansMap) {
  const combined = {};

  [boughtPlansMap, selectedPlansMap].forEach(obj => {
    for (const [ , [ , category, , billingCycle]] of Object.entries(obj)) {
      combined[category] = combined[category] || [];
      combined[category].push(billingCycle);
    }
  });

  return Object.keys(combined).reduce((prev, category) => {
    prev[category] = combined[category].some(billingCycle => ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(billingCycle));
    return prev;
  }, {});
}

function addPlanTypes(getters, planList) {
  const _getPlanCodeMap = getPlanCodeMap(getters, [planTypes.ZOOM_PHONE$ADDITIONAL, planTypes.ZONE_PRO_PLUS]);
  const boughtPlansMap = _getPlanCodeMap(getters.boughtPlans.filter(p => p.type === planTypes.ZOOM_PHONE$ADDITIONAL || PRO_PLUS_PLAN_CODES_SUPPORTED.includes(p.plan_code)));
  const selectedPlansMap = _getPlanCodeMap(getters.getSelectedPlans);

  const unSupportBillingCycleMap = getUnsupportBillingCycleCategoryMap(boughtPlansMap, selectedPlansMap)

  return planList.map(p => {
    const [, category, planType] = selectedPlansMap[p.planCode] || [];
    if (category && unSupportBillingCycleMap[category]) {
      if (p.oldPlanCode) {
        Object.assign(p, { planType, oldPlanType: planType })
      } else {
        Object.assign(p, { planType })
      }
    }
    return p;
  })
}

export function getProPlusEditForm(getters) {
  const getPlanInfoByCode = planCode => getters.planMapFlat.find(item => item.plan_code === planCode);
  const proPlusEditPlansFilter = x => (
    de$type(x.type) === planTypes.ZONE_PRO_PLUS ||
    (de$type(x.type) === planTypes.ZOOM_PHONE && [PBX_SUB_TYPES.TOLLFREE, PBX_SUB_TYPES.PHONE_NUMBER].includes(x.sub_type) && x.region === PBX_REGIONS.US_AND_CANADA) ||
    (de$type(x.type) === planTypes.ZOOM_PHONE && PBX_SUB_TYPES.BASE === x.sub_type)
  );
  const boughtPlans = getters.boughtPlans ?? [];

  const changedPlans = (getters.selectedPlanListPayLoad ?? []).filter(item => {
    const p = getPlanInfoByCode(item.planCode);
    if (!p) return true;
    return proPlusEditPlansFilter(p);
  });

  const changedPlancodes = changedPlans.map(x => x.planCode);

  const boughtPlansOfEditType = boughtPlans.filter(proPlusEditPlansFilter);
  const onlineSupportPlans = getters.planMapFlat.filter(proPlusEditPlansFilter);

  const deletedPlans = [];

  boughtPlansOfEditType.forEach(x => {
    //if smb customer and planMapFlat not have this plan, means online not support, shouldn't to remove
    if (!onlineSupportPlans.some(p => p.plan_code === x.plan_code)) {
      return;
    }
    if (!changedPlancodes.includes(x.plan_code)) {
      deletedPlans.push({
        quantity: 0,
        planCode: x.plan_code,
      });
    }
  });

  const appliedSpecialSupportCoupon = couponSourceTypeMap?.isSupportCoupon(getters?.couponState?.couponSourceType);
  const fromCampaignLink = getters.fromCampaignLink;

  // deletedPlans: edit & remove plan
  // but what about the online non-supported plans, they can be mis-canceled
  // should we record every delete action from user ? it is complcaited and not reliable
  // for now, backend has a filter for OPC non-supported plans
  const payload = [...changedPlans, ...deletedPlans].map(item => {
    const oldPlan = boughtPlans.find(p => p.plan_code === item.planCode)
    if (oldPlan) {
      // for coupon cases
      if ((item.quantity && (appliedSpecialSupportCoupon || fromCampaignLink))) {
        const discountPlans = getters.discountWithAmountMap?.discountPlans;
        if (discountPlans?.[item.planCode]) {
          return {
            ...item,
            oldPlanCode: item.planCode,
          };
        }
      }
      // filter no change plan.
      if (oldPlan.cartInfo?.selected_number === item.quantity) return null;
      return {
        ...item,
        oldPlanCode: item.planCode,
      };
    }
    return item;
  }).filter(Boolean);

  return {
    planList: addPlanTypes(getters, payload),
    actionType: UpdateTypes.UPDATE_PRO_PLUS,
  };
}
